import { useEffect, useMemo, useState } from "react";

import moment, { type Moment, type MomentInput } from "moment-timezone";

import type { MaintenanceBannerVariant } from "./MaintenanceBanner.pres";

export const TIMEZONE = "America/New_York";
const UPDATE_INTERVAL_MS = 60 * 1000; // 1 minute

const MESSAGE_VARIANTS: Record<MaintenanceBannerVariant, string> = {
  maintenance:
    "Some services may be unavailable. We apologize for any inconvenience this may cause.",
  completed: "Trading API maintenance complete!",
};

const COMPLETED_TITLE = "All systems operational";

const getSecondSaturdayForMonth = (reference: Moment): Moment => {
  const monthStart = reference.clone().tz(TIMEZONE).startOf("month");
  const firstSaturday = monthStart.clone().day(6);
  // Ensure the first Saturday we use is inside the current month before adding a week.
  const normalizedFirstSaturday =
    firstSaturday.month() !== monthStart.month()
      ? firstSaturday.clone().add(7, "days")
      : firstSaturday;

  return normalizedFirstSaturday.clone().add(7, "days");
};

type MaintenanceWindow = {
  secondSaturday: Moment;
  maintenanceStart: Moment;
  completedWindowStart: Moment;
  completedWindowEnd: Moment;
};

export const getMonthlyMaintenanceWindow = (
  reference: Moment,
): MaintenanceWindow => {
  const secondSaturday = getSecondSaturdayForMonth(reference);
  // 48hrs before maintenance window
  const maintenanceStart = secondSaturday
    .clone()
    .subtract(48, "hours")
    .startOf("day");
  // 10am start of completed window
  const completedWindowStart = secondSaturday
    .clone()
    .hour(10)
    .minute(0)
    .second(0)
    .millisecond(0);
  // 11am end of completed window
  const completedWindowEnd = completedWindowStart.clone().add(1, "hour");

  return {
    secondSaturday,
    maintenanceStart,
    completedWindowStart,
    completedWindowEnd,
  };
};

type BannerState = {
  variant: MaintenanceBannerVariant;
  title: string;
  message: string;
};

const formatMaintenanceTitle = (secondSaturday: Moment) => {
  return `System Maintenance: Saturday ${secondSaturday.format(
    "MMMM D",
  )}, 9 - 10 am ET.`;
};

export type CustomMaintenanceWindow = {
  start: MomentInput;
  end: MomentInput;
  variants: Record<
    MaintenanceBannerVariant,
    { title: string; message: string }
  >;
};

export const getBannerState = (
  now: Moment,
  customMaintenanceWindows?: CustomMaintenanceWindow[],
): BannerState | null => {
  if (customMaintenanceWindows?.length) {
    for (const maintenanceWindow of customMaintenanceWindows) {
      const start = moment(maintenanceWindow.start);
      const end = moment(maintenanceWindow.end);

      if (now.isSameOrAfter(start) && now.isBefore(end)) {
        return {
          variant: "maintenance",
          ...maintenanceWindow.variants.maintenance,
        };
      }

      if (now.isSameOrAfter(end) && now.isBefore(end.clone().add(1, "hour"))) {
        return {
          variant: "completed",
          ...maintenanceWindow.variants.completed,
        };
      }
    }
  }

  const {
    secondSaturday,
    maintenanceStart,
    completedWindowStart,
    completedWindowEnd,
  } = getMonthlyMaintenanceWindow(now);

  if (
    now.isSameOrAfter(maintenanceStart) &&
    now.isBefore(completedWindowStart)
  ) {
    return {
      variant: "maintenance",
      title: formatMaintenanceTitle(secondSaturday),
      message: MESSAGE_VARIANTS.maintenance,
    };
  }

  if (
    now.isSameOrAfter(completedWindowStart) &&
    now.isBefore(completedWindowEnd)
  ) {
    return {
      variant: "completed",
      title: COMPLETED_TITLE,
      message: MESSAGE_VARIANTS.completed,
    };
  }

  return null;
};

const getLocalizedNow = () => moment.tz(TIMEZONE);

export interface MaintenanceBannerControllerProps {
  customMaintenanceWindows?: CustomMaintenanceWindow[];
}

export const useMaintenanceBannerController = ({
  customMaintenanceWindows,
}: MaintenanceBannerControllerProps) => {
  const [now, setNow] = useState<Moment>(() => getLocalizedNow());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(getLocalizedNow());
    }, UPDATE_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const bannerState = useMemo(
    () => getBannerState(now, customMaintenanceWindows),
    [now, customMaintenanceWindows],
  );

  return {
    bannerState,
  };
};
